import React, { useState, useEffect } from "react";
import axios from "axios";
import { IoAdd, IoClose, IoPencil, IoTrash } from "react-icons/io5";
import { Button, Tooltip } from "@mui/material";
import { BASE_URL } from "../../Constants";
import { PERMISSIONS } from "../PermissionsConstant";
import PermissionDenied from "../Permission/PermissionDenied";
import SortIcon from "../sorticon";
import { useRef } from "react";

const TaskComponent = () => {
  const [tasks, setTasks] = useState([]);
  const [title, setTitle] = useState("");
  const [shortForm, setShortForm] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [error, setError] = useState("");
  const [wup_tid, setWupTid] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [errors, setErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [editTaskData, setEditTaskData] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  // Tab state
  const [activeTab, setActiveTab] = useState("tasks");

  // Task Profile state
  const [profileName, setProfileName] = useState("");
  const [profiles, setProfiles] = useState([]); // Local state for saved profiles
  const [profileError, setProfileError] = useState("");
  const [profileSuccess, setProfileSuccess] = useState("");
  const [profileLoading, setProfileLoading] = useState(false);

  // Add state for selected tasks in Task Profiles tab
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);

  // State for all profiles fetched from backend
  const [allProfiles, setAllProfiles] = useState([]);
  const [allProfilesLoading, setAllProfilesLoading] = useState(false);
  const [allProfilesError, setAllProfilesError] = useState("");
  const hasFetchedProfiles = useRef(false);

  // State to track expanded profiles for collapsible task list
  const [expandedProfiles, setExpandedProfiles] = useState([]);

  const toggleProfileExpand = (profileId) => {
    setExpandedProfiles((prev) =>
      prev.includes(profileId)
        ? prev.filter((id) => id !== profileId)
        : [...prev, profileId]
    );
  };

  const handleSort = () => {
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  // Filter tasks based on search query
  const filteredTasks = tasks.filter(
    (task) =>
      task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      task.shortForm.toLowerCase().includes(searchQuery.toLowerCase()) ||
      task.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      task.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Sort filtered tasks
  const sortedTasks = [...filteredTasks].sort((a, b) => {
    const titleA = a.title || "";
    const titleB = b.title || "";
    return sortOrder === "asc"
      ? titleA.localeCompare(titleB)
      : titleB.localeCompare(titleA);
  });

  // Categories for the task
  const categories = [
    "development",
    "design",
    "documentation",
    "testing",
    "management",
    "meeting",
    "other",
  ];

  // Axios function to create a task profile
  const createTaskProfile = async (body) => {
    // Validation
    if (!body.taskProfileName || !body.taskProfileName.trim()) {
      throw new Error("Profile name is required.");
    }
    if (
      !body.taskIds ||
      !Array.isArray(body.taskIds) ||
      body.taskIds.length === 0
    ) {
      throw new Error("At least one task must be selected.");
    }
    // API call
    try {
      // const response = await axios.post(`${BASE_URL}/api/taskrouter/task-profiles`, body);
      const response = await axios.post(
        `${BASE_URL}/api/taskprofiles/task-profiles`,
        body
      );
      return response.data;
    } catch (err) {
      throw (
        err.response?.data?.message ||
        err.message ||
        "Failed to create task profile."
      );
    }
  };

  // Handle profile save for task profiles
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setProfileError("");
    setProfileSuccess("");
    setProfileLoading(true);
    if (!profileName.trim()) {
      setProfileError("Profile name is required.");
      setProfileLoading(false);
      return;
    }
    if (selectedTaskIds.length === 0) {
      setProfileError("Select at least one task.");
      setProfileLoading(false);
      return;
    }
    // Check for duplicate profile name
    if (profiles.some((p) => p.name === profileName.trim())) {
      setProfileError("Profile name already exists.");
      setProfileLoading(false);
      return;
    }
    const requestBody = {
      taskProfileName: profileName.trim(),
      taskIds: selectedTaskIds,
    };
    try {
      await createTaskProfile(requestBody);
      setProfileSuccess("Profile saved successfully!");
      setProfiles((prev) => [
        ...prev,
        { name: profileName.trim(), taskIds: selectedTaskIds },
      ]);
      setProfileName("");
      setSelectedTaskIds([]);
      setProfileLoading(false);
    } catch (err) {
      setProfileError(err.toString());
      setProfileLoading(false);
    }
  };

  // Fetch all tasks
  const fetchAllTasks = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/taskrouter/tasks`);
      setTasks(response.data);
    } catch (err) {
      console.error("Error fetching tasks:", err);
    }
  };

  // Handle form submission for creating a task
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title || !shortForm || !description || !category) {
      setError("Please fill in all fields.");
      return;
    }

    try {
      const response = await axios.post(`${BASE_URL}/api/taskrouter/tasks`, {
        title,
        shortForm,
        description,
        category,
        wup_tid,
      });

      setSuccess("Task created successfully!");
      setError("");
      // Fetch all tasks again to include the newly created task
      fetchAllTasks();
      // Clear form fields
      setTitle("");
      setShortForm("");
      setDescription("");
      setCategory("");
      setWupTid("");
    } catch (err) {
      setError("Failed to create task. Please try again.");
      setSuccess("");
    }
  };

  useEffect(() => {
    fetchAllTasks();
  }, []);

  // Fetch all profiles from backend when All Profiles tab is selected
  useEffect(() => {
    if (activeTab === "allProfiles" && !hasFetchedProfiles.current) {
      setAllProfilesLoading(true);
      setAllProfilesError("");
      axios
        .get(`${BASE_URL}/api/taskprofiles/task-profiles`)
        .then((res) => {
          setAllProfiles(res.data);
          setAllProfilesLoading(false);
          hasFetchedProfiles.current = true;
        })
        .catch((err) => {
          setAllProfilesError(
            err.response?.data?.message ||
              err.message ||
              "Failed to fetch profiles."
          );
          setAllProfilesLoading(false);
        });
    }
    if (activeTab !== "allProfiles") {
      hasFetchedProfiles.current = false;
    }
  }, [activeTab]);

  const resetForm = () => {
    setTitle("");
    setShortForm("");
    setDescription("");
    setCategory("");
    setWupTid("");
    setShowForm(false);
  };

  const validateForm = (editTaskData) => {
    let errors = {};
    if (!editTaskData.title) {
      errors.title = "Task title is required.";
    }
    if (!editTaskData.shortForm) {
      errors.shortForm = "Short form is required.";
    }
    if (!editTaskData.description) {
      errors.description = "Description is required.";
    }
    if (
      !editTaskData.category ||
      ![
        "development",
        "rnd",
        "design",
        "documentation",
        "testing",
        "management",
        "meeting",
        "other",
      ].includes(editTaskData.category)
    ) {
      errors.category =
        "Invalid category. Please select a valid task category.";
    }
    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUpdateTask = async (e) => {
    e.preventDefault();
    if (!validateForm(editTaskData)) return;

    const updatePayload = {
      title: editTaskData.title,
      shortForm: editTaskData.shortForm,
      description: editTaskData.description,
      category: editTaskData.category,
      wup_tid: editTaskData.wup_tid,
      status:editTaskData.status,
      createdAt: new Date(),
    };

    try {
      setLoading(true);
      await axios.put(
        `${BASE_URL}/api/taskrouter/tasks/UpdateTask/${editTaskData._id}`,
        updatePayload
      );
      setSuccess("Task updated successfully!");
      setIsEditing(false);
      fetchAllTasks();
    } catch (error) {
      console.error("Error updating task:", error.response || error);
      alert("Failed to update task.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this task?"
    );
    if (!confirmDelete) return;

    try {
      setLoading(true);
      await axios.delete(
        `${BASE_URL}/api/taskrouter/tasks/DeleteTask/${taskId}`
      );
      alert("Task deleted successfully.");
      window.location.reload();
    } catch (error) {
      console.error("Error deleting task:", error.response || error);
      alert("Failed to delete task.");
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (task) => {
    setEditTaskData({ ...task });
    setIsEditing(true);
  };

  const closeModal = () => {
    setIsEditing(false);
    setEditTaskData(null);
    setErrors({});
  };

  // Handler for selecting/deselecting a single task
  const handleTaskCheckbox = (taskId) => {
    setSelectedTaskIds((prev) =>
      prev.includes(taskId)
        ? prev.filter((id) => id !== taskId)
        : [...prev, taskId]
    );
  };

  // Handler for select all
  const handleSelectAllTasks = () => {
    if (selectedTaskIds.length === sortedTasks.length) {
      setSelectedTaskIds([]);
    } else {
      setSelectedTaskIds(sortedTasks.map((task) => task._id));
    }
  };

  // const permissions = localStorage.getItem("permissions");
  const permissions = JSON.parse(localStorage.getItem("permissions"));
  console.log("Permissions");
  return (
    <div className="container mx-auto p-4 text-[#333333]">
      {/* Tab Buttons */}
      <div className="flex mb-6 border-b">
        <button
          className={`px-4 py-2 font-semibold focus:outline-none ${
            activeTab === "tasks"
              ? "border-b-2 border-[#FF480F] text-[#FF480F]"
              : "text-gray-500"
          }`}
          onClick={() => setActiveTab("tasks")}
        >
          Tasks
        </button>
        <button
          className={`ml-4 px-4 py-2 font-semibold focus:outline-none ${
            activeTab === "profiles"
              ? "border-b-2 border-[#FF480F] text-[#FF480F]"
              : "text-gray-500"
          }`}
          onClick={() => setActiveTab("profiles")}
        >
          Task Profiles
        </button>
        <button
          className={`ml-4 px-4 py-2 font-semibold focus:outline-none ${
            activeTab === "allProfiles"
              ? "border-b-2 border-[#FF480F] text-[#FF480F]"
              : "text-gray-500"
          }`}
          onClick={() => setActiveTab("allProfiles")}
        >
          All Profiles
        </button>
        <button
          className={`ml-4 px-4 py-2 font-semibold focus:outline-none ${
            activeTab === "assignProfile"
              ? "border-b-2 border-[#FF480F] text-[#FF480F]"
              : "text-gray-500"
          }`}
          onClick={() => setActiveTab("assignProfile")}
        >
          Assign Profile
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === "tasks" ? (
        permissions.includes(PERMISSIONS.TASK_PERMISSIONS.VIEW_TASKS) ? (
          <div className="container mx-auto p-4 text-[#333333]">
            <h1 className="text-3xl font-bold mb-4 font-montserrat">
              Task Management
            </h1>

            <div className="mb-4">
              <input
                type="text"
                placeholder="Search tasks..."
                className="w-full p-2 border border-gray-300 rounded"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Task List Table */}
            <div className="grid grid-cols-1 gap-6">
              {tasks.length === 0 ? (
                <p>No tasks available.</p>
              ) : (
                <table className="table-auto w-full border-collapse border border-gray-300 shadow-lg text-sm">
                  <thead>
                    <tr className="bg-gray-100 text-left">
                      <th className="border p-4">
                        <div className="flex items-center space-x-2">
                          <span>Title</span>
                          <SortIcon
                            sortOrder={sortOrder}
                            onClick={handleSort}
                          />
                        </div>
                      </th>
                      <th className="border p-4">Description</th>
                      <th className="border p-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTasks.map((task, index) => (
                      <tr
                        key={task._id}
                        className={`${
                          index % 2 === 0 ? "bg-white" : "bg-gray-50"
                        } hover:bg-gray-100 cursor-pointer`}
                      >
                        <td className="border p-4">{task.title}</td>
                        <td className="border p-4">{task.description}</td>
                        <td className="p-4 flex space-x-2 justify-center">
                          <IoPencil
                            className="text-blue-600 hover:text-blue-800 cursor-pointer"
                            onClick={() => openEditModal(task)}
                          />
                          <IoTrash
                            className="text-red-600 hover:text-red-800 cursor-pointer"
                            onClick={() => handleDeleteTask(task._id)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {permissions.includes(PERMISSIONS.TASK_PERMISSIONS.CREATE_TASK) && (
              <button onClick={() => setShowForm(true)}>
                <IoAdd className="text-xl font-bold bg-[#FF480F] text-white rounded-xl p-1 w-[3rem] h-[3rem] fixed right-20 bottom-20" />
              </button>
            )}

            {/* Create Task Form */}
            {showForm && (
              <div className="bg-gray-800/80 fixed left-0 right-0 top-0 bottom-0 flex justify-center items-center">
                <div className="bg-white md:w-1/2  shadow-md p-10 rounded-xl">
                  <div className="flex justify-between items-center">
                    <h2 className="text-xl font-semibold mb-2 font-montserrat">
                      Create New Task
                    </h2>
                    <IoClose
                      className="text-2xl cursor-pointer"
                      onClick={resetForm}
                    />
                  </div>
                  {error && <p className="text-red-500">{error}</p>}
                  {success && <p className="text-green-500">{success}</p>}
                  <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block font-medium mb-2 font-montserrat">
                          Title
                        </label>
                        <input
                          type="text"
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                          className="w-full p-2 border rounded"
                        />
                      </div>

                      <div>
                        <label className="block font-medium mb-2 font-montserrat">
                          Short Form
                        </label>
                        <input
                          type="text"
                          value={shortForm}
                          onChange={(e) => setShortForm(e.target.value)}
                          className="w-full p-2 border rounded"
                        />
                      </div>

                      <div className="col-span-2">
                        <label className="block font-medium mb-2 font-montserrat">
                          Description
                        </label>
                        <textarea
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          className="w-full p-2 border rounded"
                          rows={4}
                        />
                      </div>

                      <div>
                        <label className="block font-medium mb-2 font-montserrat">
                          Category
                        </label>
                        <select
                          value={category}
                          onChange={(e) => setCategory(e.target.value)}
                          className="w-full p-2 border rounded"
                        >
                          <option value="">Select Category</option>
                          {categories.map((cat) => (
                            <option key={cat} value={cat}>
                              {cat.charAt(0).toUpperCase() + cat.slice(1)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block font-medium mb-2 font-montserrat">
                          Wup TaskId
                        </label>
                        <input
                          type="text"
                          value={wup_tid}
                          onChange={(e) => setWupTid(e.target.value)}
                          className="w-full p-2 border rounded"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end mt-4">
                      <button
                        type="submit"
                        className="bg-[#FF480F] text-white px-4 py-2 rounded"
                      >
                        Create Task
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Edit Task Modal */}
            {isEditing && editTaskData && (
              <div className="bg-gray-800/80 fixed left-0 right-0 top-0 bottom-0 flex justify-center items-center">
                <div className="bg-white w-full shadow-md p-10 rounded-xl">
                  <div className="flex justify-between items-center">
                    <h2 className="text-xl font-semibold mb-2 font-montserrat">
                      Edit Task
                    </h2>
                    <IoClose
                      className="text-2xl cursor-pointer"
                      onClick={closeModal}
                    />
                  </div>
                  {errors.title && (
                    <p className="text-red-500">{errors.title}</p>
                  )}
                  {errors.shortForm && (
                    <p className="text-red-500">{errors.shortForm}</p>
                  )}
                  {errors.description && (
                    <p className="text-red-500">{errors.description}</p>
                  )}
                  {errors.category && (
                    <p className="text-red-500">{errors.category}</p>
                  )}
                  {success && <p className="text-green-500">{success}</p>}
                  <form onSubmit={handleUpdateTask}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block font-medium mb-2 font-montserrat">
                          Title
                        </label>
                        <input
                          type="text"
                          value={editTaskData.title}
                          onChange={(e) =>
                            setEditTaskData({
                              ...editTaskData,
                              title: e.target.value,
                            })
                          }
                          className="w-full p-2 border rounded"
                        />
                      </div>
                      <div>
                        <label className="block font-medium mb-2 font-montserrat">
                          Short Form
                        </label>
                        <input
                          type="text"
                          value={editTaskData.shortForm}
                          onChange={(e) =>
                            setEditTaskData({
                              ...editTaskData,
                              shortForm: e.target.value,
                            })
                          }
                          className="w-full p-2 border rounded"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block font-medium mb-2 font-montserrat">
                          Description
                        </label>
                        <textarea
                          value={editTaskData.description}
                          onChange={(e) =>
                            setEditTaskData({
                              ...editTaskData,
                              description: e.target.value,
                            })
                          }
                          className="w-full p-2 border rounded"
                          rows={4}
                        />
                      </div>
                      <div>
                        <label className="block font-medium mb-2 font-montserrat">
                          Category
                        </label>
                        <select
                          value={editTaskData.category}
                          onChange={(e) =>
                            setEditTaskData({
                              ...editTaskData,
                              category: e.target.value,
                            })
                          }
                          className="w-full p-2 border rounded"
                        >
                          <option value="">Select Category</option>
                          {categories.map((cat) => (
                            <option key={cat} value={cat}>
                              {cat.charAt(0).toUpperCase() + cat.slice(1)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block font-medium mb-2 font-montserrat">
                          Wup TaskId
                        </label>
                        <input
                          type="text"
                          value={editTaskData.wup_tid}
                          onChange={(e) =>
                            setEditTaskData({
                              ...editTaskData,
                              wup_tid: e.target.value,
                            })
                          }
                          className="w-full p-2 border rounded"
                        />
                      </div>
                         <div>
                        <label className="block font-medium mb-2 font-montserrat">
                          Task Status
                        </label>
                        <select
                          value={editTaskData.status}
                          onChange={(e) =>
                            setEditTaskData({
                              ...editTaskData,
                              status: e.target.value,
                            })
                          }
                          className="w-full p-2 border rounded"
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex justify-end mt-4">
                      <button
                        type="button"
                        className="bg-gray-300 px-4 py-2 rounded mr-2"
                        onClick={closeModal}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="bg-[#FF480F] text-white px-4 py-2 rounded"
                      >
                        Update Task
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        ) : (
          <PermissionDenied />
        )
      ) : activeTab === "profiles" ? (
        // Task Profile Tab: Create a task profile by selecting tasks
        <div className="w-full  mx-auto bg-white p-8 rounded-xl  ">
          <h2 className="text-xl font-bold mb-4 font-montserrat">
            Create Task Profile
          </h2>
          {profileError && <p className="text-red-500 mb-2">{profileError}</p>}
          {profileSuccess && (
            <p className="text-green-500 mb-2">{profileSuccess}</p>
          )}
          <form onSubmit={handleSaveProfile}>
            <div className="mb-4">
              <label className="block font-medium mb-2 font-montserrat">
                Profile Name
              </label>
              <input
                type="text"
                value={profileName}
                onChange={(e) => setProfileName(e.target.value)}
                className="w-full p-2 border rounded"
                placeholder="Enter profile name"
              />
            </div>
            <div className="mb-4">
              <label className="block font-medium mb-2 font-montserrat">
                Select Tasks
              </label>
              <div className="w-full">
                {tasks.length === 0 ? (
                  <p>No tasks available.</p>
                ) : (
                  <>
                    <label className="flex items-center space-x-2 font-semibold mb-2">
                      <input
                        type="checkbox"
                        checked={
                          selectedTaskIds.length === sortedTasks.length &&
                          sortedTasks.length > 0
                        }
                        onChange={handleSelectAllTasks}
                        aria-label="Select all tasks"
                      />
                      <span>Select All</span>
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 w-full">
                      {sortedTasks.map((task) => (
                        <label
                          key={task._id}
                          className="flex items-center space-x-2 p-2 bg-gray-50 rounded"
                        >
                          <input
                            type="checkbox"
                            checked={selectedTaskIds.includes(task._id)}
                            onChange={() => handleTaskCheckbox(task._id)}
                            aria-label={`Select task ${task.title}`}
                          />
                          <span>{task.title}</span>
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                className="bg-[#FF480F] text-white px-4 py-2 rounded flex items-center"
                disabled={
                  !profileName.trim() ||
                  selectedTaskIds.length === 0 ||
                  profileLoading
                }
              >
                {profileLoading && <span className="loader mr-2"></span>}
                Save Profile
              </button>
              {/* Simple CSS spinner */}
              <style>{`
                .loader {
                  border: 2px solid #f3f3f3;
                  border-top: 2px solid #FF480F;
                  border-radius: 50%;
                  width: 16px;
                  height: 16px;
                  animation: spin 1s linear infinite;
                  display: inline-block;
                }
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          </form>
          {/* List of saved profiles */}
          {profiles.length > 0 && (
            <div className="mt-8">
              <h3 className="text-lg font-semibold mb-2 font-montserrat">
                Saved Profiles
              </h3>
              <ul className="space-y-2">
                {profiles.map((profile, idx) => (
                  <li key={profile.name + idx} className="border p-3 rounded">
                    <div className="font-bold">{profile.name}</div>
                    <div className="text-sm text-gray-600">
                      Task IDs: {profile.taskIds.join(", ")}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : activeTab === "allProfiles" ? (
        // All Profiles Tab
        <div className="w-full mx-auto bg-white p-8 rounded-xl">
          <h2 className="text-xl font-bold mb-4 font-montserrat">
            All Task Profiles
          </h2>
          {allProfilesLoading ? (
            <div>Loading profiles...</div>
          ) : allProfilesError ? (
            <div className="text-red-500">{allProfilesError}</div>
          ) : allProfiles.length === 0 ? (
            <p>No profiles found.</p>
          ) : (
            <ul className="space-y-4">
              {allProfiles.map((profile, idx) => {
                const isExpanded = expandedProfiles.includes(
                  profile._id || profile.name || idx
                );
                return (
                  <li
                    key={profile._id || profile.name || idx}
                    className="border p-4 rounded shadow-sm"
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-bold text-lg mb-2">
                        {profile.name || profile.taskProfileName}
                      </div>
                      <button
                        className="text-[#FF480F] font-semibold px-2 py-1 rounded hover:underline focus:outline-none"
                        onClick={() =>
                          toggleProfileExpand(
                            profile._id || profile.name || idx
                          )
                        }
                      >
                        {isExpanded ? "Hide Tasks" : "Show Tasks"}
                      </button>
                    </div>
                    {isExpanded && (
                      <>
                        <div className="text-sm text-gray-700 mb-1">Tasks:</div>
                        <ul className="list-disc list-inside ml-4">
                          {(profile.taskIds || []).map((task) => (
                            <li key={task._id}>{task.title}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : activeTab === "assignProfile" ? (
        // Assign Profile Tab
        <div className="w-full mx-auto bg-white p-8 rounded-xl">
          <h2 className="text-xl font-bold mb-4 font-montserrat">
            Assign Profile to User
          </h2>
          <AssignProfileForm />
        </div>
      ) : null}
    </div>
  );
};

// AssignProfileForm component
function AssignProfileForm() {
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState("");
  const [profiles, setProfiles] = useState([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [profilesError, setProfilesError] = useState("");
  const [selectedProfileId, setSelectedProfileId] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState("");
  const [assignSuccess, setAssignSuccess] = useState("");

  // Fetch users and profiles on mount
  useEffect(() => {
    setUsersLoading(true);
    setUsersError("");
    axios
      .get("https://timesheet-be.fleetstudio.com/api/user/getUsers")
      .then((res) => {
        setUsers(res.data);
        setUsersLoading(false);
      })
      .catch((err) => {
        setUsersError(
          err.response?.data?.message || err.message || "Failed to fetch users."
        );
        setUsersLoading(false);
      });
    setProfilesLoading(true);
    setProfilesError("");
    axios
      .get(`${BASE_URL}/api/taskprofiles/task-profiles`)
      .then((res) => {
        setProfiles(res.data);
        setProfilesLoading(false);
      })
      .catch((err) => {
        setProfilesError(
          err.response?.data?.message ||
            err.message ||
            "Failed to fetch profiles."
        );
        setProfilesLoading(false);
      });
  }, []);

  const handleAssign = async (e) => {
    e.preventDefault();
    setAssignError("");
    setAssignSuccess("");
    if (!selectedUserId) {
      setAssignError("Please select a user.");
      return;
    }
    if (!selectedProfileId) {
      setAssignError("Please select a profile.");
      return;
    }
    setAssignLoading(true);
    try {
      await axios.put(
        `${BASE_URL}/api/user/assign-task-profile`,
        {
          userId: selectedUserId,
          taskProfileId: selectedProfileId,
        },
        {
          headers: { "Content-Type": "application/json" },
        }
      );
      setAssignSuccess("Profile assigned successfully!");
    } catch (err) {
      setAssignError(
        err.response?.data?.message ||
          err.message ||
          "Failed to assign profile."
      );
    } finally {
      setAssignLoading(false);
    }
  };

  if (usersLoading || profilesLoading) {
    return <div>Loading users and profiles...</div>;
  }
  if (usersError) {
    return <div className="text-red-500">{usersError}</div>;
  }
  if (profilesError) {
    return <div className="text-red-500">{profilesError}</div>;
  }
  if (users.length === 0 || profiles.length === 0) {
    return <div>No users or profiles found. Please create them first.</div>;
  }

  return (
    <form onSubmit={handleAssign} className="max-w-lg mx-auto space-y-4">
      {assignError && <div className="text-red-500">{assignError}</div>}
      {assignSuccess && <div className="text-green-600">{assignSuccess}</div>}
      <div>
        <label className="block font-medium mb-1">Select User</label>
        <select
          className="w-full p-2 border rounded"
          value={selectedUserId}
          onChange={(e) => setSelectedUserId(e.target.value)}
        >
          <option value="">Select a user</option>
          {users.map((user) => (
            <option key={user._id} value={user._id}>
              {user.fname} {user.lname} ({user.email})
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="block font-medium mb-1">Select Profile</label>
        <select
          className="w-full p-2 border rounded"
          value={selectedProfileId}
          onChange={(e) => setSelectedProfileId(e.target.value)}
        >
          <option value="">Select a profile</option>
          {profiles.map((profile) => (
            <option key={profile._id} value={profile._id}>
              {profile.name || profile.taskProfileName}
            </option>
          ))}
        </select>
      </div>
      <div className="flex justify-end">
        <button
          type="submit"
          className="bg-[#FF480F] text-white px-4 py-2 rounded flex items-center"
          disabled={assignLoading}
        >
          {assignLoading && <span className="loader mr-2"></span>}
          Assign
        </button>
      </div>
    </form>
  );
}

export default TaskComponent;
